
import { useState } from 'react';
import { ProfileData } from '@/pages/Profile';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { Eye, EyeOff, Key } from 'lucide-react';

interface ProfileSettingsProps {
  profileData: ProfileData;
  updateProfile: (data: Partial<ProfileData>) => Promise<void>;
}

const ProfileSettings = ({ profileData, updateProfile }: ProfileSettingsProps) => {
  const [isEditingApiKey, setIsEditingApiKey] = useState(false);
  const [apiKey, setApiKey] = useState(profileData.youtube_api_key || '');
  const [showApiKey, setShowApiKey] = useState(false);

  const handleSubmitApiKey = async (e: React.FormEvent) => {
    e.preventDefault();
    await updateProfile({
      youtube_api_key: apiKey || null
    });
    setIsEditingApiKey(false);
  };

  const maskApiKey = (key: string | null) => {
    if (!key) return "Non définie";
    if (key.length <= 8) return "*".repeat(key.length);
    return key.substring(0, 4) + "*".repeat(key.length - 8) + key.substring(key.length - 4);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Paramètres du profil</h2>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            Clé API YouTube
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <p className="font-medium">
                {showApiKey 
                  ? profileData.youtube_api_key || "Non définie"
                  : maskApiKey(profileData.youtube_api_key)
                }
              </p>
              <p className="text-sm text-muted-foreground">
                Utilisée pour les requêtes vers l'API YouTube
              </p>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => setShowApiKey(!showApiKey)}
                title={showApiKey ? "Masquer la clé" : "Afficher la clé"}
              >
                {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
              <Button 
                variant="outline"
                onClick={() => setIsEditingApiKey(true)}
              >
                Modifier
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={isEditingApiKey} onOpenChange={setIsEditingApiKey}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier votre clé API YouTube</DialogTitle>
            <DialogDescription>
              Entrez votre clé API YouTube pour accéder aux fonctionnalités de l'application.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmitApiKey} className="space-y-4 py-4">
            <div className="space-y-2">
              <label htmlFor="apiKey" className="text-sm font-medium">
                Clé API
              </label>
              <div className="flex items-center space-x-2">
                <Key className="h-4 w-4 text-muted-foreground" />
                <Input
                  id="apiKey"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="Entrez votre clé API YouTube"
                  type={showApiKey ? "text" : "password"}
                />
                <Button 
                  variant="ghost" 
                  size="icon"
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                >
                  {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Cette clé est nécessaire pour effectuer des requêtes vers l'API YouTube.
              </p>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEditingApiKey(false)}>
                Annuler
              </Button>
              <Button type="submit">Enregistrer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProfileSettings;
